import { z } from "zod"
import { CountryCode } from "./country-data"
import {
    SHIPPING_COUNTRIES,
    ShippingCountry,
    ShippingCountryKey,
    getCountryKeyFromCountryValue
} from "./shipping-countries"

export const SHIPPING_COUNTRY_CODES = {
    Austria: "at",
    Belgium: "be",
    Bulgaria: "bg",
    Croatia: "hr",
    Cyprus: "cy",
    CzechRepublic: "cz",
    Denmark: "dk",
    Estonia: "ee",
    Finland: "fi",
    France: "fr",
    Germany: "de",
    Greece: "gr",
    Hungary: "hu",
    Ireland: "ie",
    Italy: "it",
    Latvia: "lv",
    Lithuania: "lt",
    Luxembourg: "lu",
    Malta: "mt",
    Netherlands: "nl",
    Norway: "no",
    Poland: "pl",
    Portugal: "pt",
    Romania: "ro",
    Slovakia: "sk",
    Slovenia: "si",
    Spain: "es",
    Sweden: "se",
    Switzerland: "ch",
    UnitedKingdom: "gb",
    Canada: "ca",
    USA: "us",
    Australia: "au",
    NewZealand: "nz",
    Japan: "jp"
} as const

export type ShippingCountryCode =
    (typeof SHIPPING_COUNTRY_CODES)[keyof typeof SHIPPING_COUNTRY_CODES]

export const isShippingCountryCode = (
    value: string,
    shippingCountryCodes: typeof SHIPPING_COUNTRY_CODES
): value is ShippingCountryCode => {
    return Object.values(shippingCountryCodes).includes(
        value.toLowerCase() as ShippingCountryCode
    )
}

export const mapShippingCountryCodeToCountryCode = (
    shippingCode: ShippingCountryCode
): CountryCode => shippingCode.toUpperCase() as CountryCode

const getCountryCodeByCountryNameCurried =
    (
        shippingCountryCodes: typeof SHIPPING_COUNTRY_CODES,
        shippingCountries: typeof SHIPPING_COUNTRIES,
        getCountryKey: (countryValue: ShippingCountry) => ShippingCountryKey | undefined
    ) =>
    (
        countryName: ShippingCountry | ShippingCountryKey
    ): ShippingCountryCode | undefined => {
        const isCountryValue = Object.values(shippingCountries).includes(
            countryName as ShippingCountry
        )

        const countryKey = isCountryValue
            ? getCountryKey(countryName as ShippingCountry)
            : countryName

        return countryKey
            ? shippingCountryCodes[countryKey as ShippingCountryKey]
            : undefined
    }

export const getStatesByCountryCode = (countries: Country[], countryCode: string) => {
    const country = countries.find((c: Country) => c.country === countryCode)
    return country?.states || []
}

export const getCountryCodeByCountryName = getCountryCodeByCountryNameCurried(
    SHIPPING_COUNTRY_CODES,
    SHIPPING_COUNTRIES,
    getCountryKeyFromCountryValue
)

/* Zod Runtime Types For Shipping Country Code */

const allShippingCountryCodes = Object.values(SHIPPING_COUNTRY_CODES) as [
    ShippingCountryCode,
    ...ShippingCountryCode[]
]

const ShippingCountryCodeSchema = z.enum(allShippingCountryCodes)

export const validateShippingCountryCode = (code: string) =>
    ShippingCountryCodeSchema.safeParse(code.toLowerCase())
